import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type Ref,
} from 'react';
import { Icon } from './Icon';
import './password-input.css';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Required: say which password a browser should offer. `current-password`
   * for one that exists, `new-password` for one being set, `off` for a
   * secret that is not the user's own (an app password, an API key).
   */
  autoComplete: string;
  /** The input itself, as a plain `<input>` ref would be. */
  ref?: Ref<HTMLInputElement>;
  /** Class for the wrapper, for layouts that size the field (flex rows). */
  wrapperClassName?: string;
  /**
   * The toggle's name. "Show password" unless the secret is not a password
   * ("Show API key"): the button says what it would show.
   */
  revealLabel?: string;
}

/**
 * A password field with a Show / Hide toggle (ADR 0094).
 *
 * Every prop goes to the `<input>`, `ref` included; only `type` is this
 * component's. The toggle:
 *
 * - is a `type="button"` so it never submits, and a toggle button
 *   (`aria-pressed`) named "Show password" that controls the input
 *   (`aria-controls`) -- the name stays put and the pressed state carries the
 *   change, so a screen reader never hears "Hide password, pressed";
 * - does not take focus from the field when pressed with a pointer, and puts
 *   the caret or selection back where it was, so typing carries on;
 * - hides the text again when its form is submitted or reset, when the app
 *   itself replaces the value (a save that clears the field), and when the
 *   field unmounts (the state goes with it);
 * - never writes the value anywhere else: nothing is logged and nothing is
 *   announced, and spell-check is off, because a shown password is plain text
 *   that a browser's cloud spell-checker would otherwise send away.
 */
export function PasswordInput({
  ref,
  id: idProp,
  wrapperClassName,
  revealLabel = 'Show password',
  className,
  onChange,
  value,
  disabled,
  ...rest
}: PasswordInputProps) {
  const fallbackId = useId();
  const id = idProp ?? `${fallbackId}-password`;
  const [shown, setShown] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Where the caret was when the toggle was pressed, if the field had focus. */
  const restore = useRef<{
    start: number | null;
    end: number | null;
    direction: 'forward' | 'backward' | 'none' | null;
  } | null>(null);
  const focusedAtPress = useRef(false);
  /** The last value the USER produced; any other change came from the app. */
  const typed = useRef(value);

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') return ref(node);
      if (ref) (ref as { current: HTMLInputElement | null }).current = node;
      return undefined;
    },
    [ref],
  );

  // Hidden again on submit or reset. A native listener on the form: it runs
  // before React's delegated onSubmit, and it covers every caller's form
  // without each of them remembering to.
  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) return;
    const hide = () => setShown(false);
    form.addEventListener('submit', hide);
    form.addEventListener('reset', hide);
    return () => {
      form.removeEventListener('submit', hide);
      form.removeEventListener('reset', hide);
    };
  }, []);

  // The app replaced the value (a save or a failure that clears the field):
  // whatever was shown is done with. A change the user typed does not count.
  useEffect(() => {
    if (value !== typed.current) setShown(false);
    typed.current = value;
  }, [value]);

  useLayoutEffect(() => {
    const at = restore.current;
    restore.current = null;
    const input = inputRef.current;
    if (!at || !input) return;
    if (document.activeElement !== input) input.focus({ preventScroll: true });
    const { start, end, direction } = at;
    if (start === null || end === null) return;
    const put = () => {
      if (document.activeElement !== input) return;
      if (input.selectionStart === start && input.selectionEnd === end) return;
      input.setSelectionRange(start, end, direction ?? undefined);
    };
    put();
    // Chromium rebuilds the field's inner editor for the new type at the
    // next layout and, after a real (trusted) pointer press, drops the
    // selection to 0 there -- after this effect. Put it back once that has
    // happened, or the next key typed lands at the start (seen on testing,
    // 2026-09-28; a synthetic click does not reproduce it).
    let frameAfter = 0;
    const frame = requestAnimationFrame(() => {
      put();
      frameAfter = requestAnimationFrame(put);
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(frameAfter);
    };
  }, [shown]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    typed.current = event.currentTarget.value;
    onChange?.(event);
  };

  const toggle = () => {
    const input = inputRef.current;
    // A pointer press left focus in the field (mousedown is prevented), so
    // the caret goes back there. A keyboard press is ON the toggle, and focus
    // stays with it.
    if (input && (focusedAtPress.current || document.activeElement === input)) {
      restore.current = {
        start: input.selectionStart,
        end: input.selectionEnd,
        direction: input.selectionDirection,
      };
    }
    focusedAtPress.current = false;
    setShown((now) => !now);
  };

  return (
    <div className={wrapperClassName ? `password-input ${wrapperClassName}` : 'password-input'}>
      <input
        {...rest}
        ref={setRefs}
        id={id}
        className={className ?? 'input'}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
      />
      <button
        type="button"
        className="btn btn--icon password-input__toggle"
        aria-label={revealLabel}
        aria-pressed={shown}
        aria-controls={id}
        disabled={disabled}
        onPointerDown={() => {
          focusedAtPress.current = document.activeElement === inputRef.current;
        }}
        onMouseDown={(event) => {
          // Keep focus (and a phone's keyboard) in the field.
          event.preventDefault();
        }}
        onClick={toggle}
      >
        <Icon name={shown ? 'eye-off' : 'eye'} />
      </button>
    </div>
  );
}
